/**
 * <lyle-product-review-stars>
 * -----------------------------------------------------------------------------
 * Scrolls the customer to the product reviews widget when the star rating is
 * clicked. The rating and the reviews widget are both provided by the
 * AfterShip / Automizely Reviews app, which injects its widget asynchronously
 * after page load — so the scroll target is resolved at click time, not on
 * connect. If no reviews widget is present, the click is a no-op.
 */
class LyleProductReviewStars extends HTMLElement {
  #abortController = null;
  #layoutController = null;

  static REVIEWS_SELECTORS = [
    "#aftership_reviews_carousel_v2_production",
    "[id^='aftership_reviews']",
    "#shopify-product-reviews",
    ".reviews-section-wrapper",
    "lyle-reviews-section",
  ];

  connectedCallback() {
    this.setAttribute("role", "button");
    this.setAttribute("tabindex", "0");
    this.classList.add("product-review-stars--interactive");

    const imgEl = this.querySelector('[role="img"]');
    const ariaLabel = imgEl?.getAttribute("aria-label");
    if (ariaLabel) this.setAttribute("aria-label", ariaLabel);
    if (imgEl) imgEl.setAttribute("aria-hidden", "true");

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener("click", this.#scrollToReviews, { signal });
    this.addEventListener("keydown", this.#handleKeydown, { signal });
  }

  disconnectedCallback() {
    this.#layoutController?.abort();
    this.#layoutController = null;
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #getReviewsTarget() {
    for (const selector of LyleProductReviewStars.REVIEWS_SELECTORS) {
      const target = document.querySelector(selector);
      if (target) {
        return target;
      }
    }

    return null;
  }

  #handleKeydown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.#scrollToReviews();
    }
  };

  #scrollToReviews = (event) => {
    event?.preventDefault();

    const target = this.#getReviewsTarget();
    if (!target) {
      return;
    }

    this.#followLayoutShifts(target);
  };

  #followLayoutShifts(target) {
    this.#layoutController?.abort();

    const scroll = () =>
      target.scrollIntoView({
        block: "start",
        inline: "nearest",
        behavior: "smooth",
      });

    scroll();
    if (!("ResizeObserver" in window)) return;

    const controller = new AbortController();
    const observer = new ResizeObserver(() => {
      if (!target.isConnected) {
        controller.abort();
        return;
      }

      scroll();
    });
    const timeout = setTimeout(() => controller.abort(), 4000);
    const cancel = () => controller.abort();

    this.#layoutController = controller;
    observer.observe(document.body);
    controller.signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timeout);
        observer.disconnect();
        if (this.#layoutController === controller) this.#layoutController = null;
      },
      { once: true },
    );

    for (const type of ["wheel", "touchstart", "pointerdown", "keydown"]) {
      window.addEventListener(type, cancel, {
        once: true,
        signal: controller.signal,
      });
    }
  }
}

if (!customElements.get("lyle-product-review-stars")) {
  customElements.define("lyle-product-review-stars", LyleProductReviewStars);
}
